import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';

/**
 * Error `code` values returned by subscriptionWriteMiddleware and usage-limit paths
 * (Lambda bodies from createResponse with JSON { success, code, message, ... }).
 */
export const SUBSCRIPTION_AND_USAGE_ERROR_CODES = new Set<string>([
  'NO_SUBSCRIPTION',
  'TRIAL_EXPIRED',
  'SUBSCRIPTION_EXPIRED',
  'FEATURE_NOT_IN_PLAN',
  'LIMIT_EXCEEDED',
  'SUBSCRIPTION_GUARD_ERROR',
  'INVALID_JSON'
]);

const TITLE_BY_CODE: Record<string, string> = {
  NO_SUBSCRIPTION: 'Subscription required',
  TRIAL_EXPIRED: 'Trial ended',
  SUBSCRIPTION_EXPIRED: 'Subscription expired',
  FEATURE_NOT_IN_PLAN: 'Not included in your plan',
  LIMIT_EXCEEDED: 'Usage limit reached',
  SUBSCRIPTION_GUARD_ERROR: 'Could not verify subscription',
  INVALID_JSON: 'Invalid request'
};

export function parseApiErrorPayload(error: HttpErrorResponse): Record<string, unknown> | null {
  const e = error.error;
  if (e == null) {
    return null;
  }
  if (typeof e === 'string') {
    try {
      const parsed = JSON.parse(e) as unknown;
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof e === 'object' && !Array.isArray(e)) {
    return e as Record<string, unknown>;
  }
  return null;
}

export function getSubscriptionUsageErrorCode(error: HttpErrorResponse): string | undefined {
  const payload = parseApiErrorPayload(error);
  if (!payload) {
    return undefined;
  }
  const code = payload['code'];
  return typeof code === 'string' ? code : undefined;
}

export function isSubscriptionOrUsageLimitApiError(error: HttpErrorResponse): boolean {
  const code = getSubscriptionUsageErrorCode(error);
  return typeof code === 'string' && SUBSCRIPTION_AND_USAGE_ERROR_CODES.has(code);
}

/**
 * Shows a single user-facing toast for subscription / usage errors. Call from HTTP interceptors
 * so callers still receive the HttpErrorResponse (this does not swallow the error).
 *
 * @returns true if a toast was shown (recognized code).
 */
export function showSubscriptionUsageLimitToastIfApplicable(
  toast: ToastrService,
  error: HttpErrorResponse
): boolean {
  if (!isSubscriptionOrUsageLimitApiError(error)) {
    return false;
  }

  const payload = parseApiErrorPayload(error);
  const code = getSubscriptionUsageErrorCode(error) ?? 'LIMIT_EXCEEDED';
  const rawMessage = payload?.['message'];
  const message =
    typeof rawMessage === 'string' && rawMessage.trim().length > 0
      ? rawMessage
      : 'This action is not allowed with your current subscription or usage limits.';

  const title = TITLE_BY_CODE[code] ?? 'Plan limitation';

  toast.error(message, title, {
    timeOut: 9000,
    extendedTimeOut: 4000,
    tapToDismiss: true,
    closeButton: true
  });

  return true;
}
